import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useFetchCompanyInfoApi, usePostLoginApi } from '@api/useLoginApi';

import {
  setLocalStorage,
  removeLocalStorage,
} from '@services/localStorageService';
import { LocalStorageKeyConstants } from '@constants/general/localStorageKeyConstants';
import { UserInformationModel } from '@models/login/userInformationModel';
import { PublicRoutes } from '@enums/publicRouteEnums';
import { PrivateRoutes } from '@enums/privateRouteEnums';
import { AppContextConstants } from '@constants/appContextConstants';
import { AppContextType } from '@context/appContextType';

import { getInitialState } from '@services/authStateService';
import { setGlobal401Handler } from '@services/httpService';
import { useMediaQuery, useTheme } from '@mui/material';
import { CompanyResponseModel } from '@models/login/companyResponseModel';
import { LoginResponseModel } from '@models/login/loginResponseModel';
import { useRoutePersistence } from '@hooks/useRoutePersistence';
import { getLastRoute } from '@services/routePersistenceService';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionLoginApi } from '@hooks/api/useSessionLoginApi';
import { useCrossTabAppSync } from '@hooks/useCrossTabAppSync';

// Returns the key as it was actually written in the URL, so a redirect can rewrite that same
// parameter rather than normalising it to a different spelling.
const findCompanyIdParam = (
  searchParams: URLSearchParams
): { key: string; value: string } | null => {
  const companyParams = [
    AppContextConstants.COID,
    AppContextConstants.COMPANY_ID,
  ];

  for (const param of companyParams) {
    const value = searchParams.get(param);
    if (value) return { key: param, value };

    for (const [key, val] of searchParams.entries()) {
      if (key.toLowerCase() === param.toLowerCase()) {
        return { key, value: val };
      }
    }
  }

  return null;
};

const extractCompanyId = (searchParams: URLSearchParams): string =>
  findCompanyIdParam(searchParams)?.value ?? AppContextConstants.EMPTY_STRING;

const AppContext = createContext<AppContextType>({} as AppContextType);
export function AppProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInformationModel | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyResponseModel | null>(
    null
  );
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isAuthResolved, setIsAuthResolved] = useState(false);
  const [companyId, setCompanyId] = useState(AppContextConstants.EMPTY_STRING);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.down('lg'));
  const { restoreLastRoute, clearHistory, setIntentionalPath } =
    useRoutePersistence();

  const coid = useMemo(() => {
    return extractCompanyId(searchParams);
  }, [searchParams]);

  const { data: companyInformation } = useFetchCompanyInfoApi(companyId);

  const {
    mutateAsync: login,
    data: loginData,
    reset: resetLogin,
  } = usePostLoginApi();

  const {
    mutateAsync: sessionLoginApi,
    data: sessionLoginData,
    reset: resetSessionLogin,
  } = useSessionLoginApi();

  useEffect(() => {
    const loadInitialState = async () => {
      try {
        const state = await getInitialState();
        setUser(state.user);
        // Only restore stored company info, never clear it. This effect re-runs on every
        // searchParams change, and there is nothing stored before login, so assigning
        // unconditionally wipes the company info the company-info query just loaded.
        if (state.companyInfo) {
          setCompanyInfo(state.companyInfo);
        }
        setIsAuthorized(state.isAuthorized);

        // Set companyId from stored state or from URL parameters
        const urlCompanyId = extractCompanyId(searchParams);
        const finalCompanyId =
          state.companyId || urlCompanyId || AppContextConstants.EMPTY_STRING;
        setCompanyId(finalCompanyId);

        if (state.isAuthorized && location.pathname === PrivateRoutes.ROOT) {
          try {
            const lastRoute = await getLastRoute();
            if (
              lastRoute &&
              lastRoute.path !== PrivateRoutes.ROOT &&
              lastRoute.path !== location.pathname
            ) {
              setIntentionalPath(PrivateRoutes.ROOT);
              navigate(lastRoute.path, { replace: true });
            }
          } catch {}
        }
      } finally {
        // Must run even if reading stored state throws, or guards would wait
        // forever and the app would render nothing.
        setIsAuthResolved(true);
      }
    };
    loadInitialState();
  }, [location.pathname, navigate, searchParams, isAuthorized]);

  useCrossTabAppSync({
    isAuthorized,
    user,
    setUser,
    setIsAuthorized,
    setCompanyInfo,
    setCompanyId,
    resetLogin,
    clearHistory,
  });

  const handleTokenExpired = useCallback(() => {
    if (isAuthorized) {
      // Preserve companyId for URL before clearing state
      const currentCompanyId = companyId;
      const loginUrl = currentCompanyId
        ? `${PublicRoutes.LOGIN}?${AppContextConstants.COID}=${currentCompanyId}`
        : PublicRoutes.LOGIN;

      setUser(null);
      setIsAuthorized(false);
      setCompanyId(AppContextConstants.EMPTY_STRING);
      removeLocalStorage(AppContextConstants.AUTH_STORAGE_KEY);
      sessionStorage.removeItem(
        LocalStorageKeyConstants.ANNOUNCEMENT_DISMISSED
      );
      resetLogin();
      clearHistory();

      window.location.href = loginUrl;
    }
  }, [navigate, isAuthorized, resetLogin, clearHistory, companyId]);

  useEffect(() => {
    setGlobal401Handler(handleTokenExpired);
  }, [handleTokenExpired]);

  const handleCompanyIdUpdate = useCallback(() => {
    if (isAuthorized && location.pathname === PublicRoutes.LOGIN) {
      return;
    }

    if (
      (location.pathname === PrivateRoutes.ROOT ||
        location.pathname === PublicRoutes.LOGIN ||
        location.pathname === PublicRoutes.FORGOT_PASSWORD ||
        location.pathname === PublicRoutes.RESET_PASSWORD ||
        location.pathname === PublicRoutes.DEFAULT) &&
      !isAuthorized
    ) {
      // Always try to extract companyId from URL parameters when on login/root pages
      const extractedCompanyId = extractCompanyId(searchParams);
      if (extractedCompanyId && extractedCompanyId !== companyId) {
        setCompanyId(extractedCompanyId);
        // Don't clear companyInfo when just changing companyId - let the API call handle it
      }
    }
  }, [coid, location.pathname, searchParams, isAuthorized, companyId]);

  useEffect(() => {
    handleCompanyIdUpdate();
  }, [
    handleCompanyIdUpdate,
    coid,
    location.pathname,
    searchParams,
    isAuthorized,
    companyId,
  ]);

  useEffect(() => {
    if (companyInformation) {
      setCompanyInfo(companyInformation);
    }
  }, [companyInformation]);

  // A retired companyId resolves to its replacement on the server. Reflect that in the URL so the
  // address bar - and anything copied or bookmarked from it - carries the new one. Replaces the
  // entry rather than pushing, so Back does not return to the retired id.
  useEffect(() => {
    const resolvedCompanyId = companyInformation?.companyId;
    if (!resolvedCompanyId) return;

    const companyIdParam = findCompanyIdParam(searchParams);
    // Only rewrite a companyId the URL already carries; never add one it did not have.
    if (!companyIdParam || companyIdParam.value === resolvedCompanyId) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(companyIdParam.key, resolvedCompanyId);

    setCompanyId(resolvedCompanyId);
    navigate(
      { pathname: location.pathname, search: nextParams.toString() },
      { replace: true }
    );
  }, [companyInformation, searchParams, navigate, location.pathname]);

  useEffect(() => {
    if (loginData?.isValidUser) {
      // Use companyId from session login response
      const targetCompanyId = loginData.companyId || coid || companyId;

      if (targetCompanyId !== companyId) {
        setCompanyId(targetCompanyId);
        return;
      }

      if (targetCompanyId && !companyInformation) {
        return;
      }

      const newAuthState = {
        user: loginData.userInformation,
        isAuthorized: true,
        companyId: loginData.companyId || targetCompanyId,
        companyInfo: companyInformation,
      };
      setUser(loginData.userInformation);
      setIsAuthorized(true);
      setLocalStorage(AppContextConstants.AUTH_STORAGE_KEY, newAuthState).then(
        async () => {
          if (location.pathname === PublicRoutes.LOGIN) {
            await restoreLastRoute();
            if (location.pathname === PublicRoutes.LOGIN) {
              navigate(PrivateRoutes.ROOT);
            }
          }
        }
      );
    }
  }, [
    loginData,
    companyInformation,
    companyId,
    navigate,
    location.pathname,
    restoreLastRoute,
    coid,
  ]);

  useEffect(() => {
    if (sessionLoginData?.isValidUser && !isAuthorized) {
      // Use companyId from session login response
      const targetCompanyId = sessionLoginData.companyId || coid || companyId;

      if (targetCompanyId !== companyId) {
        setCompanyId(targetCompanyId);
        return;
      }

      if (targetCompanyId && !companyInformation) {
        return;
      }

      const newAppState = {
        user: sessionLoginData.userInformation,
        isAuthorized: true,
        companyId: sessionLoginData.companyId || targetCompanyId,
        companyInfo: companyInformation,
      };

      setUser(sessionLoginData.userInformation);
      setIsAuthorized(true);

      setLocalStorage(AppContextConstants.AUTH_STORAGE_KEY, newAppState).then(
        () => {
          navigate(PrivateRoutes.ROOT);
        }
      );
    }
  }, [
    sessionLoginData,
    navigate,
    isAuthorized,
    coid,
    companyId,
    companyInformation,
  ]);

  const loginUser = useCallback(
    async (company: string, account: string, pin: string) => {
      const targetCompanyId = company || coid || companyId;
      // Set companyId before login so company info can start fetching
      setCompanyId(targetCompanyId);
      return await login({ companyId: targetCompanyId, account, pin });
    },
    [coid, companyId, login]
  );

  const loginWithSession = useCallback(
    async (sessionToken: string) => {
      await sessionLoginApi(sessionToken);
    },
    [sessionLoginApi]
  );

  const logout = useCallback(() => {
    try {
      // Preserve companyId for URL before clearing state
      const currentCompanyId = companyId;
      const loginUrl = currentCompanyId
        ? `${PublicRoutes.LOGIN}?${AppContextConstants.COMPANY_ID}=${currentCompanyId}`
        : PublicRoutes.LOGIN;

      resetLogin();
      resetSessionLogin();
      clearHistory();
      setUser(null);
      setIsAuthorized(false);

      // Clear storage and query cache
      removeLocalStorage(AppContextConstants.AUTH_STORAGE_KEY);
      sessionStorage.removeItem(
        LocalStorageKeyConstants.ANNOUNCEMENT_DISMISSED
      );
      queryClient.clear();

      window.location.href = loginUrl;
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = PublicRoutes.LOGIN;
    }
  }, [companyId, queryClient, resetLogin, resetSessionLogin, clearHistory]);

  const completeLoginFromResponse = useCallback(
    async (response: LoginResponseModel) => {
      if (!response?.isValidUser || !response.userInformation) return;
      const targetCompanyId =
        response.companyId || companyId;
      if (targetCompanyId) setCompanyId(targetCompanyId);
      setUser(response.userInformation);
      setIsAuthorized(true);
      const newAuthState = {
        user: response.userInformation,
        isAuthorized: true,
        companyId: targetCompanyId,
        companyInfo: companyInformation ?? null,
      };
      await setLocalStorage(AppContextConstants.AUTH_STORAGE_KEY, newAuthState);
      await restoreLastRoute();
      navigate(PrivateRoutes.ROOT);
    },
    [
      companyId,
      companyInformation,
      navigate,
      restoreLastRoute,
    ]
  );

  const contextValue = useMemo(
    () => ({
      user,
      isAuthorized,
      isAuthResolved,
      loginUser,
      loginWithSession,
      completeLoginFromResponse,
      logout,
      companyId,
      setCompanyId,
      companyInfo,
      isMobileScreen,
      isTabletScreen,
      isSmallDesktop,
    }),
    [
      user,
      isAuthorized,
      isAuthResolved,
      loginUser,
      loginWithSession,
      completeLoginFromResponse,
      logout,
      companyId,
      setCompanyId,
      companyInfo,
      isMobileScreen,
      isTabletScreen,
      isSmallDesktop,
    ]
  );
  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}
export const useApp = () => useContext(AppContext);
